import React from 'react';
import type { AnchorHTMLAttributes, ButtonHTMLAttributes, ComponentProps, PropsWithChildren } from 'react';
import { Link } from 'react-router-dom';

import { cn } from '@/utils/cn';

interface TextCommons extends Omit<React.HTMLAttributes<HTMLSpanElement>, 'content'> {
    $themeColor?: ThemeTextOptions;
    $maxWidth?: string;
    $width?: string;
    $truncated?: boolean;
    $uppercase?: boolean;
    $italic?: boolean;
    $centered?: boolean;
    $strikethrough?: boolean;
}

export type TextProps = TextCommons & {
    onClick?: (ev: React.MouseEvent<HTMLElement>) => void;
} & (
        | { largeBody: boolean; body?: never; helper?: never }
        | { largeBody?: never; body: boolean; helper?: never }
        | { largeBody?: never; body?: never; helper: boolean }
        | { largeBody?: never; body?: never; helper?: never }
    );

const roleClasses = {
    largeBody: 'text-base font-normal -mt-0.5',
    body: 'text-sm font-light -mt-0.5',
    helper: 'text-xs font-light -mt-0.5',
    page: 'text-xl font-medium mt-0',
    modal: 'text-lg font-medium mt-0',
    section: 'text-lg font-medium mt-0',
    content: 'text-base font-medium mt-0',
    form: 'text-sm font-normal mt-0',
};

const themeColorMap: Record<ThemeTextOptions, string> = {
    primary: 'text-[#2C2424]',
    subtle: 'text-[#716664]',
    disabled: 'text-[#A09793]',
    positive: 'text-[#077440]',
    negative: 'text-[#B03731]',
    white: 'text-[#f8f7f5]',
    highlight: 'text-[#946A3E]',
    link: 'text-[#155DFC]',
    linkHighlight: 'text-[#193CB8]',
};

export const Text = ({
    children,
    onClick,
    largeBody,
    body,
    helper,
    $themeColor,
    $maxWidth,
    $width,
    $truncated,
    $uppercase,
    $italic,
    $centered,
    $strikethrough,
    ...props
}: PropsWithChildren<TextProps>) => {
    const role = largeBody ? 'largeBody' : helper ? 'helper' : 'body';
    const clickable = !!onClick;

    const classes = cn(
        roleClasses[role],
        clickable && 'cursor-pointer hover:text-[#193CB8] hover:underline',
        $themeColor ? themeColorMap[$themeColor] : clickable ? 'text-[#155DFC]' : 'text-inherit',
        $truncated && 'w-full whitespace-nowrap overflow-hidden text-ellipsis',
        $uppercase && 'uppercase',
        $italic && 'italic',
        $centered && 'text-center',
        $strikethrough && 'line-through',
    );

    return (
        <span
            className={classes}
            onClick={onClick}
            style={{
                maxWidth: $maxWidth,
                width: $width,
            }}
            {...props}
        >
            {children}
        </span>
    );
};

type TitleProps = TextCommons & {
    onClick?: (ev: React.MouseEvent<HTMLElement>) => void;
} & (
        | { page: boolean; modal?: never; section?: never; content?: never; form?: never }
        | { page?: never; modal: boolean; section?: never; content?: never; form?: never }
        | { page?: never; modal?: never; section: boolean; content?: never; form?: never }
        | { page?: never; modal?: never; section?: never; content: boolean; form?: never }
        | { page?: never; modal?: never; section?: never; content?: never; form: boolean }
    );

export const Title = ({
    children,
    onClick,
    page,
    modal,
    section,
    content,
    form,
    $themeColor,
    $maxWidth,
    $width,
    $truncated,
    $uppercase,
    $italic,
    $centered,
    $strikethrough,
    ...props
}: PropsWithChildren<TitleProps>) => {
    const role = page ? 'page' : modal ? 'modal' : section ? 'section' : form ? 'form' : 'content';
    const clickable = !!onClick;

    const classes = cn(
        roleClasses[role],
        clickable && 'cursor-pointer hover:text-[#193CB8] hover:underline',
        $themeColor ? themeColorMap[$themeColor] : clickable ? 'text-[#155DFC]' : 'text-inherit',
        $truncated && 'w-full whitespace-nowrap overflow-hidden text-ellipsis',
        $uppercase && 'uppercase',
        $italic && 'italic',
        $centered && 'text-center',
        $strikethrough && 'line-through',
    );

    return (
        <span className={classes} onClick={onClick} style={{ maxWidth: $maxWidth, width: $width }} {...props}>
            {children}
        </span>
    );
};

type TextLinkProps =
    | ({ to: ComponentProps<typeof Link>['to'] } & ComponentProps<typeof Link>)
    | ({ to?: never; as?: never } & AnchorHTMLAttributes<HTMLAnchorElement>)
    | ({ to?: never; as: 'button' } & ButtonHTMLAttributes<HTMLButtonElement>);

export const TextLink = (props: TextLinkProps) => {
    const { className, children } = props;
    const classes = cn(
        'mt-[-2px] cursor-pointer font-light text-blue-600 no-underline hover:text-blue-800 hover:underline',
        className,
    );

    if ('to' in props && props.to) {
        const { className: _className, children: _children, ...linkProps } = props;
        return (
            <Link className={classes} {...linkProps}>
                {children}
            </Link>
        );
    }

    if ('as' in props && props.as === 'button') {
        const { as: _as, className: _className, children: _children, type, ...buttonProps } = props;
        return (
            <button className={classes} type={type ?? 'button'} {...buttonProps}>
                {children}
            </button>
        );
    }

    const { className: _className, children: _children, ...anchorProps } = props;
    return (
        <a className={classes} {...anchorProps}>
            {children}
        </a>
    );
};
